import React, { useEffect, useState } from "react";
import { TextField, Grid, FormControl, InputLabel, Switch, FormControlLabel, Card, Typography } from "@mui/material";
import moment from "moment";
import { DoctorReportDto } from "../../../../api-client";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";

interface DoctorReportProps {
  doctorReport: DoctorReportDto;
  setDoctorReport: any;
  editMode?: boolean;
}

const PrintingAndShippingProtocolCard = ({
  doctorReport,
  setDoctorReport,
  editMode,
}: DoctorReportProps) => {
  const [showModal, setShowModal] = useState(false);
  const [processingDate, setProcessingDate] = useState<string>("");
  const [printedAt, setPrintedAt] = useState<string>("");
  const [sentByEmailAt, setSentByEmailAt] = useState<string>("");
  const [sentByEmailAtTime, setSentByEmailAtTime] = useState<string>("");
  const [sentByFaxAt, setSentByFaxAt] = useState<string>("");
  const [sentByFaxAtTime, setSentByFaxAtTime] = useState<string>("");
  const [IsPrintedAtDisabled, setPrintedAtDisabled] = useState<boolean>(true);
  const [IsSentByFaxAtDisabled, setSentByFaxAtDisabled] = useState<boolean>(true);
  const [IsSentByEmailDisabled, setSentByEmailDisabled] = useState<boolean>(true);

  const [dateTimePresentationProp, setDateTimePresentationProp] = useState("date");
  const [currentInput, setCurrentInput] = useState<
    | "processingDate"
    | "printedAt"
    | "sentByEmailAt"
    | "sentByEmailAtTime"
    | "sentByFaxAt"
    | "sentByFaxAtTime"
    | null
  >(null);
  const dateTimeFormat = "dd.MM.yyyy";

  useEffect(() => {
    if (doctorReport) {
      if (doctorReport.processingDate)
        setProcessingDate(moment(doctorReport.processingDate).format(dateTimeFormat));
      if (doctorReport.printedAt) {
        setPrintedAt(moment(doctorReport.printedAt).format(dateTimeFormat));
        setPrintedAtDisabled(false);
      }

      if (doctorReport.sentByEmailAt) {
        setSentByEmailAt(moment(doctorReport.sentByEmailAt).format(dateTimeFormat));
        setSentByEmailAtTime(moment(doctorReport.sentByEmailAt).format("HH:mm"));
        setSentByEmailDisabled(false);
      }

      if (doctorReport.sentByFaxAt) {
        setSentByFaxAt(moment(doctorReport.sentByFaxAt).format(dateTimeFormat));
        setSentByFaxAtTime(moment(doctorReport.sentByFaxAt).format("HH:mm"));
        setSentByFaxAtDisabled(false);
      }
    }
  }, [doctorReport]);

  const handleChangeDoctorReport = (key: string, value: any) => {
    setDoctorReport({ ...doctorReport, [key]: value });
  };

  // Merge a newly picked date (keeping the existing time) or a newly picked
  // time (keeping the existing date) into the value already stored on the
  // doctor report. Reading the current value directly from `doctorReport`
  // avoids stale local-state and invalid-format bugs that caused the date to
  // reset when editing the time.
  const handleDateChange = (
    picked: any,
    input:
      | "processingDate"
      | "printedAt"
      | "sentByEmailAt"
      | "sentByEmailAtTime"
      | "sentByFaxAt"
      | "sentByFaxAtTime"
  ) => {
    const pickedMoment = moment(picked);
    if (!pickedMoment.isValid()) return;

    switch (input) {
      case "processingDate":
        handleChangeDoctorReport("processingDate", pickedMoment.format("YYYY-MM-DD"));
        break;
      case "printedAt":
        handleChangeDoctorReport("printedAt", pickedMoment.format("YYYY-MM-DD"));
        break;
      case "sentByEmailAt": {
        const existing = doctorReport.sentByEmailAt ? moment(doctorReport.sentByEmailAt) : null;
        const combined = pickedMoment.clone();
        if (existing?.isValid()) combined.hour(existing.hour()).minute(existing.minute());
        handleChangeDoctorReport("sentByEmailAt", combined.format("YYYY-MM-DD HH:mm:ss"));
        break;
      }
      case "sentByEmailAtTime": {
        const existing = doctorReport.sentByEmailAt ? moment(doctorReport.sentByEmailAt) : moment();
        const base = existing.isValid() ? existing.clone() : moment();
        base.hour(pickedMoment.hour()).minute(pickedMoment.minute()).second(0);
        handleChangeDoctorReport("sentByEmailAt", base.format("YYYY-MM-DD HH:mm:ss"));
        break;
      }
      case "sentByFaxAt": {
        const existing = doctorReport.sentByFaxAt ? moment(doctorReport.sentByFaxAt) : null;
        const combined = pickedMoment.clone();
        if (existing?.isValid()) combined.hour(existing.hour()).minute(existing.minute());
        handleChangeDoctorReport("sentByFaxAt", combined.format("YYYY-MM-DD HH:mm:ss"));
        break;
      }
      case "sentByFaxAtTime": {
        const existing = doctorReport.sentByFaxAt ? moment(doctorReport.sentByFaxAt) : moment();
        const base = existing.isValid() ? existing.clone() : moment();
        base.hour(pickedMoment.hour()).minute(pickedMoment.minute()).second(0);
        handleChangeDoctorReport("sentByFaxAt", base.format("YYYY-MM-DD HH:mm:ss"));
        break;
      }
    }
  };

  // Parse DTO timestamps directly to Date objects for the pickers.
  // The local state strings produced by the format/parse round-trip below
  // were unreliable (mixed date-fns vs moment tokens); these direct values
  // ensure the read-only pickers always render the actual stored values.
  const toDate = (v: any): Date | null => {
    if (!v) return null;
    const m = moment(v);
    return m.isValid() ? m.toDate() : null;
  };
  const processingDateValue = toDate(doctorReport?.processingDate);
  const printedAtValue = toDate(doctorReport?.printedAt);
  const sentByFaxAtValue = toDate(doctorReport?.sentByFaxAt);
  const sentByEmailAtValue = toDate(doctorReport?.sentByEmailAt);

  return (
    <Card>
      <Typography  sx={{
              fontWeight: "bold",
              textDecoration: "underline",
            }}>Druck-und Versandprotokoll</Typography>

      <Grid container spacing={3}>
        <Grid item xs={6}>
        <InputLabel>Bearbeitungsdatum</InputLabel>
          <DatePicker
            label=""
            value={processingDateValue}
            onChange={(date: any) => {
              setCurrentInput("processingDate");
              setDateTimePresentationProp("date");
              handleDateChange(date, "processingDate");
            }}
            format={dateTimeFormat}
            disabled={!editMode}
          />
        </Grid>

        <Grid item xs={6}>
          {/* <FormControl fullWidth> */}
            <InputLabel>Gedruckt am</InputLabel>
            <DatePicker
              label=""
              value={printedAtValue}
              onChange={(date: any) => {
                setCurrentInput("printedAt");
                setDateTimePresentationProp("date");
                handleDateChange(date, "printedAt");
              }}
              format={dateTimeFormat}
              disabled={!editMode}
            />
          {/* </FormControl> */}
        </Grid>

        <Grid item xs={12}>
          <InputLabel>per Fax</InputLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="am"
                value={sentByFaxAtValue}
                onChange={(date: any) => {
                  setCurrentInput("sentByFaxAt");
                  setDateTimePresentationProp("date");
                  handleDateChange(date, "sentByFaxAt");
                }}
                format={dateTimeFormat}
                disabled={!editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="um"
                value={sentByFaxAtValue}
                onChange={(date: any) => {
                  setCurrentInput("sentByFaxAtTime");
                  setDateTimePresentationProp("time");
                  handleDateChange(date, "sentByFaxAtTime");
                }}
                format="HH:mm"
                disabled={!editMode}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <InputLabel>per Email</InputLabel>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="am"
                value={sentByEmailAtValue}
                onChange={(date: any) => {
                  setCurrentInput("sentByEmailAt");
                  setDateTimePresentationProp("date");
                  handleDateChange(date, "sentByEmailAt");
                }}
                format={dateTimeFormat}
                disabled={true}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TimePicker
                label="um"
                value={sentByEmailAtValue}
                onChange={(date: any) => {
                  setCurrentInput("sentByEmailAtTime");
                  setDateTimePresentationProp("time");
                  handleDateChange(date, "sentByEmailAtTime");
                }}
                format="HH:mm"
                disabled={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};
export default PrintingAndShippingProtocolCard;
